import PropTypes from "prop-types";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { IconDots } from "@tabler/icons";
import ActionButton, { iconButtonSx } from "src/components/shared/ActionButton";
import DayField from "src/components/shared/DayField";
import { isoFromTyped, todayIso, typedDayError, typedFromIso } from "src/components/shared/days";
import { updateRequest } from "src/pages/request/actions";
import { casesApi } from "./api";
import { dateLong, dateTime, money } from "./format";
import { cardText, emptyText, moneyIn, outcome, receiptLines } from "./paymentWords";
import { PaymentChip, travellerLabel } from "./workflow";
import PayLaterHistory from "./PayLaterHistory";
import { Link as RouterLink } from "react-router-dom";

// The money, inside the case (QA georges-03, nadia-08): what it costs (the
// visa fee and the service fee, per traveller, and the total), whether it is
// paid, and when, how and with what reference. Each button says what it
// does before it is pressed (nadia-22): "Check with the bank" asks the
// payment provider, "Record a payment" is for cash or a transfer and asks
// how next; neither emails the customer. "Mark as not paid" sits in a menu
// and asks first, naming the case and the amount (georges-04). Below: each
// receipt the bank confirmed and each opening of the payment page.

// How the money came in (builder P: Whish and OMT for Pay later, API
// docs/pay-later.md), in the toast's words and the reference's hint.
export const RECORD_METHODS = [
  { value: "cash", label: "Cash", said: "in cash", hint: "A receipt book number, if you gave one." },
  { value: "transfer", label: "Bank transfer", said: "by bank transfer", hint: "The transfer’s reference, as the bank shows it." },
  { value: "whish", label: "Whish", said: "by Whish", hint: "The Whish transaction number." },
  { value: "omt", label: "OMT", said: "by OMT", hint: "The OMT transfer number (MTCN)." },
];
const saidOf = (how) => (RECORD_METHODS.find((m) => m.value === how) || RECORD_METHODS[0]).said;

// "2026-10-28" (a day staff gave) or an instant, for reading.
const paidOnWords = (value) => (/^\d{4}-\d{2}-\d{2}$/.test(String(value || "")) ? dateLong(value) : dateTime(value));

function Line({ label, children, strong }) {
  return (
    <>
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: strong ? 700 : 600, overflowWrap: "anywhere", fontFamily: strong ? "monospace" : undefined }}>
        {children}
      </Typography>
    </>
  );
}

Line.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node, strong: PropTypes.bool };

const NOT_RECORDED = (
  <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
    Not recorded
  </Box>
);

// What it costs: the split when the product still says what was charged.
function Costs({ fees, amount, travellers }) {
  const times = travellers > 1 ? ` × ${travellers}` : "";
  const split = fees && fees.matches;
  return (
    <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5 }}>
      {split ? (
        <>
          <Line label="Visa fee">{Number(fees.visa_fee) > 0 ? `${money(fees.visa_fee)}${times}` : "Not collected by Safrati"}</Line>
          <Line label="Service fee">{`${money(fees.service_fee)}${times}`}</Line>
          {/* "Travelling together" (API docs/travelling-together.md): off Safrati's fee only. */}
          {fees.together_off > 0 ? <Line label="Travelling together">{`−${money(fees.together_off)}`}</Line> : null}
        </>
      ) : null}
      <Line label="Total" strong>
        {money(amount)}
      </Line>
      {fees && !fees.matches ? (
        <Typography variant="body2" color="text.secondary" sx={{ gridColumn: "1 / -1" }}>
          The product’s price has changed since this application was made, so its visa fee and service fee at the time aren’t known; the total is what was charged.
        </Typography>
      ) : null}
      {fees?.separate?.amount > 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ gridColumn: "1 / -1" }}>
          Also paid by the traveller, not through Safrati: {moneyIn(fees.separate.amount, fees.separate.currency || "USD")}
          {fees.separate.note ? ` (${fees.separate.note})` : ""}.
        </Typography>
      ) : null}
    </Box>
  );
}

Costs.propTypes = { fees: PropTypes.object, amount: PropTypes.number, travellers: PropTypes.number };

export default function Payment({ caseData, visas, canEdit, onChanged }) {
  const dispatch = useDispatch();
  const [dialog, setDialog] = useState(null); // "record" | "unpaid"
  const [menu, setMenu] = useState(null);
  const [bank, setBank] = useState(null); // the bank's last answer, when not paid
  const payment = caseData.payment || {};
  const paid = payment.is_paid ?? !!caseData.is_paid;
  const amount = payment.amount ?? caseData.total;
  // A partner's application is billed to the partner, never paid online.
  const billedTo = payment.billed_to || caseData.partner?.name || null;
  const method = billedTo && !paid ? null : payment.method || caseData.payment_type || null;
  const records = Array.isArray(payment.records) ? payment.records : [];
  const attempts = Array.isArray(payment.attempts) ? payment.attempts : [];
  const empty = emptyText({ paid, records, attempts, billedTo });
  const travellers = caseData.travellers.length;
  const changeable = canEdit && caseData.status !== "incomplete" && !(billedTo && !paid);
  // An API before the case's paid details: the bank's last receipt says it.
  const receipt = payment.paid_on === undefined && records.length ? records[records.length - 1] : null;
  const paidOn = payment.paid_on ?? receipt?.paid_at ?? null;
  const reference = payment.reference ?? receipt?.receipt_number ?? null;
  const card = payment.card ? cardText(payment.card.brand, payment.card.last4) : receipt ? cardText(receipt.card_brand, receipt.card_last4) : "";

  const checkBank = async () => {
    setBank(null);
    try {
      await casesApi.markPaid(caseData.id, dispatch);
      toast.success(`The bank confirmed the payment of case #${caseData.id}: ${money(amount)}. It is marked as paid. No email was sent.`);
      await onChanged();
    } catch (err) {
      setBank(err?.message || "The bank did not confirm a payment.");
      // The check may have updated what the bank said on the latest opening.
      onChanged();
    }
  };

  // Every traveller, or one: the classic page could record one traveller's
  // payment, so the case still can.
  const save = async ({ value, only, method: how, reference, paidOn, notify = false }) => {
    const who = only ? caseData.travellers.filter((t) => t.request_id === only) : caseData.travellers;
    // Recording money: one call that takes the bank's steps (its receipt, the
    // promise, the Travelling together reward, the customer's email). An API
    // before it answers 404, and the classic path below records cash or a transfer.
    if (value) {
      const whom = only ? travellerLabel(caseData, who[0]) : `case #${caseData.id}`;
      try {
        const res = await casesApi.recordPayment(
          { id: Number(caseData.id), method: how, reference: reference || null, paid_on: paidOn || null, ...(only ? { request_id: Number(only) } : {}), notify },
          dispatch,
        );
        const d = res?.data || {};
        if (d.already) toast.info(`Nothing to record: ${whom} was already paid.`);
        else
          toast.success(
            `Payment recorded for ${whom}: ${money(d.amount ?? (only ? Number(amount) / travellers : amount))}, ${saidOf(how)}${reference ? ` (ref ${reference})` : ""}. ${
              d.emailed ? "The customer was emailed that we received it." : "No email was sent."
            }`,
          );
        setDialog(null);
        onChanged();
        return true;
      } catch (err) {
        if (err?.status !== 404) {
          toast.error(`Couldn’t record the payment for ${whom}. Nothing changed. Try again.`);
          return false;
        }
      }
    }
    const failed = [];
    for (const t of who) {
      try {
        // The current visa expiry goes along: every classic update rewrites it.
        // eslint-disable-next-line no-await-in-loop
        await updateRequest(
          { id: t.request_id, isPaid: value, paymentMethod: how, paymentReference: reference, paidOn, expireAt: visas[t.request_id]?.expire_at, notify: false, quiet: true },
          dispatch,
        );
      } catch {
        failed.push(t);
      }
    }
    const share = only ? money(Number(amount) / travellers) : money(amount);
    const whom = only ? travellerLabel(caseData, who[0]) : `case #${caseData.id}`;
    if (failed.length === who.length) {
      toast.error(`Couldn’t ${value ? "record the payment" : "mark it as not paid"} for ${whom}. Nothing changed. Try again.`);
      return false;
    }
    if (failed.length) {
      toast.error(`${failed.map((t) => travellerLabel(caseData, t)).join(" and ")} ${failed.length === 1 ? "wasn’t" : "weren’t"} updated; the others were. Try again for ${failed.length === 1 ? "that traveller" : "them"}.`);
    } else if (value) {
      toast.success(`Payment recorded for ${whom}: ${share}, ${saidOf(how)}${reference ? ` (ref ${reference})` : ""}. No email was sent.`);
    } else {
      toast.success(`${whom.replace(/^case/, "Case")} is marked as not paid: ${share} shows as owed. No email was sent.`);
    }
    setDialog(null);
    onChanged();
    return true;
  };

  return (
    <Card variant="outlined" id="case-payment" tabIndex={-1} sx={{ scrollMarginTop: 180, outline: "none" }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} mb={1}>
          <Typography variant="h6" component="h2">
            Payment
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <PaymentChip caseData={{ ...caseData, is_paid: paid, partner: billedTo ? { name: billedTo } : caseData.partner }} />
            {changeable && paid ? (
              <>
                <IconButton
                  aria-label={`More payment actions for case #${caseData.id}`}
                  aria-haspopup="menu"
                  aria-expanded={menu ? "true" : undefined}
                  onClick={(e) => setMenu(e.currentTarget)}
                  sx={iconButtonSx}
                >
                  <IconDots size={20} />
                </IconButton>
                <Menu anchorEl={menu} open={!!menu} onClose={() => setMenu(null)}>
                  <MenuItem
                    onClick={() => {
                      setMenu(null);
                      setDialog("unpaid");
                    }}
                  >
                    Mark as not paid…
                  </MenuItem>
                </Menu>
              </>
            ) : null}
          </Stack>
        </Stack>

        <Costs fees={payment.fees} amount={Number(amount)} travellers={travellers} />

        {paid ? (
          <Box component="dl" sx={{ m: 0, mt: 1.5, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 2, rowGap: 0.5 }}>
            <Line label="Paid on">{paidOn ? paidOnWords(paidOn) : NOT_RECORDED}</Line>
            <Line label="Method">{method || NOT_RECORDED}</Line>
            <Line label="Reference">{reference ? [reference, card].filter(Boolean).join(" · ") : NOT_RECORDED}</Line>
            {payment.recorded_by ? <Line label="Recorded by">{payment.recorded_by}</Line> : null}
            {/* Paid, then cancelled or refused: say where the refund stands
                (round 2, nadia-17). Nothing on the case records a refund of
                the payment itself yet, so the card says that, never "refunded". */}
            {["cancelled", "rejected"].includes(caseData.status) ? (
              <Line label="Refund">
                <Box component="span" sx={{ fontWeight: 600, color: (t) => (t.palette.mode === "dark" ? "#F5C26B" : "#8A5A00") }}>
                  Not decided here: paid {money(amount)}, then {caseData.status === "rejected" ? "refused" : "cancelled"}.
                </Box>{" "}
                No refund of this payment is recorded on the case. If you refund it, add a team note saying when and how.
                {caseData.status === "cancelled" ? (
                  <>
                    {" "}
                    <Box component={RouterLink} to="/money" sx={{ fontWeight: 600, color: "primary.main" }}>
                      Money lists it under “Paid, then cancelled”
                    </Box>
                    .
                  </>
                ) : null}
              </Line>
            ) : null}
          </Box>
        ) : billedTo ? (
          <Typography variant="body2" color="text.secondary" mt={1}>
            Billed to {billedTo}, not paid online.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" fontWeight={700} mt={1} sx={{ color: (t) => (t.palette.mode === "dark" ? "#F5C26B" : "#8A5A00") }}>
              Not paid: {money(amount)} outstanding
            </Typography>
            {payment.pay_later ? (
              <Typography variant="body2" mt={0.5}>
                {payment.pay_later_history ? null : <Chip size="small" variant="outlined" color="warning" label="Waiting for payment (Pay later)" sx={{ mr: 1 }} />}
                The customer chose to pay by Whish, OMT or cash, quoting case #{caseData.id}. Record it when it comes in.
              </Typography>
            ) : null}
            {/* Builder P: held until, the reminders, or back to a draft. */}
            <PayLaterHistory history={payment.pay_later_history || null} waiting={Boolean(payment.pay_later)} />
          </>
        )}

        {changeable && !paid ? (
          <Stack spacing={1.5} mt={1.5}>
            {payment.bank_check_available ? (
              <Box>
                <ActionButton busyLabel="Asking the bank…" onClick={checkBank} aria-describedby="case-pay-bank-what">
                  Check with the bank
                </ActionButton>
                <Typography id="case-pay-bank-what" variant="body2" color="text.secondary" mt={0.5}>
                  Asks the payment provider whether the customer paid online, and marks it paid if they did. Nothing is sent to the customer.
                </Typography>
              </Box>
            ) : null}
            <Box>
              <ActionButton variant={payment.bank_check_available ? "text" : "outlined"} onClick={() => setDialog("record")} aria-describedby="case-pay-record-what" sx={payment.bank_check_available ? { ml: -1.5 } : undefined}>
                Record a payment…
              </ActionButton>
              <Typography id="case-pay-record-what" variant="body2" color="text.secondary" mt={0.5}>
                For cash, a bank transfer, Whish or OMT: you’ll choose how it was paid and add its reference next, and whether we email the customer.
              </Typography>
            </Box>
          </Stack>
        ) : null}
        {bank && !paid && (
          <Alert severity="warning" sx={{ mt: 1.5 }} onClose={() => setBank(null)}>
            {bank}
          </Alert>
        )}

        {/* Paid: the lines above say what wasn't recorded. */}
        {empty && !paid && (
          <Typography variant="body2" color="text.secondary" mt={1.5}>
            {empty}
          </Typography>
        )}
        {records.length > 0 && <Receipts records={records} />}
        {attempts.length > 0 && <Attempts attempts={attempts} />}
      </CardContent>

      {dialog === "record" ? <RecordPayment caseData={caseData} amount={Number(amount)} onClose={() => setDialog(null)} onSave={(values) => save({ value: 1, ...values })} /> : null}
      {dialog === "unpaid" ? (
        <Dialog open onClose={() => setDialog(null)} maxWidth="xs" fullWidth aria-labelledby="case-unpaid-title">
          <DialogTitle id="case-unpaid-title">Mark case #{caseData.id} as not paid?</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              The {money(amount)} will show as owed again, and the case will ask for the payment before it is filed. Use this only to correct a mistake: the
              timeline records who did it.
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={1}>
              The customer is not emailed. To undo it, record the payment again.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
            <ActionButton variant="text" size="medium" onClick={() => setDialog(null)}>
              Keep as it is
            </ActionButton>
            <ActionButton variant="contained" size="medium" busyLabel="Saving…" onClick={() => save({ value: 0 })}>
              Mark as not paid
            </ActionButton>
          </DialogActions>
        </Dialog>
      ) : null}
    </Card>
  );
}

// Cash or a transfer received outside the website: how, its reference, the
// day it came in, and for whom (every traveller, or one).
function RecordPayment({ caseData, amount, onClose, onSave }) {
  const [how, setHow] = useState("cash");
  const [notify, setNotify] = useState(true);
  const [reference, setReference] = useState("");
  const [day, setDay] = useState(() => typedFromIso(todayIso()));
  const [only, setOnly] = useState("all");
  const [tried, setTried] = useState(false);
  const travellers = caseData.travellers.length;
  const dayError = typedDayError(day, { required: true });
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="case-record-pay-title">
      <DialogTitle id="case-record-pay-title">Record a payment for case #{caseData.id}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" mb={1.5}>
          For money received outside the website. It is marked as paid with a receipt, and counts like a card payment: the on-time promise starts and
          a Travelling together reward follows.
        </Typography>
        <Typography id="case-record-pay-how" variant="subtitle2" fontWeight={600}>
          How was it paid?
        </Typography>
        <RadioGroup value={how} onChange={(e) => setHow(e.target.value)} aria-labelledby="case-record-pay-how" sx={{ mb: 1.5, "& .MuiFormControlLabel-label": { pl: 0.5 } }}>
          {RECORD_METHODS.map((m) => (
            <FormControlLabel key={m.value} value={m.value} control={<Radio />} label={m.label} />
          ))}
        </RadioGroup>
        <TextField
          fullWidth
          label="Reference (optional)"
          helperText={(RECORD_METHODS.find((m) => m.value === how) || RECORD_METHODS[0]).hint}
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          inputProps={{ maxLength: 100 }}
          sx={{ mb: 2 }}
        />
        <DayField label="Day it was paid" value={day} onChange={setDay} error={tried ? dayError : ""} hint="Today, unless it came in earlier." />
        {travellers > 1 ? (
          <>
            <Typography id="case-record-pay-who" variant="subtitle2" fontWeight={600} mt={2}>
              For whom?
            </Typography>
            <RadioGroup value={String(only)} onChange={(e) => setOnly(e.target.value)} aria-labelledby="case-record-pay-who" sx={{ "& .MuiFormControlLabel-label": { pl: 0.5 } }}>
              <FormControlLabel value="all" control={<Radio />} label={`Every traveller: ${money(amount)}`} />
              {caseData.travellers.map((t) => (
                <FormControlLabel key={t.request_id} value={String(t.request_id)} control={<Radio />} label={`Only ${travellerLabel(caseData, t)}: ${money(amount / travellers)}`} />
              ))}
            </RadioGroup>
          </>
        ) : null}
        <FormControlLabel
          sx={{ mt: 1.5, alignItems: "flex-start", "& .MuiFormControlLabel-label": { pt: 1 } }}
          control={<Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)} />}
          label="Email the customer that we received it"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <ActionButton variant="text" size="medium" onClick={onClose}>
          Keep as it is
        </ActionButton>
        <ActionButton
          variant="contained"
          size="medium"
          busyLabel="Saving…"
          onClick={() => {
            if (dayError) {
              setTried(true);
              return undefined;
            }
            return onSave({ method: how, reference: reference.trim(), paidOn: isoFromTyped(day), only: only === "all" ? null : Number(only), notify });
          }}
        >
          Record the payment
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
}

RecordPayment.propTypes = {
  caseData: PropTypes.object.isRequired,
  amount: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

const listSx = { listStyle: "none", p: 0, m: 0 };

function Receipts({ records }) {
  return (
    <Box component="section" aria-labelledby="case-receipts-title" mt={2}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography id="case-receipts-title" variant="subtitle2" component="h3" mb={1}>
        {records.length === 1 ? "Receipt" : `Receipts (${records.length})`}
      </Typography>
      <Stack component="ul" spacing={1} sx={listSx}>
        {records.map((record) => {
          const r = receiptLines(record);
          return (
            <Box
              component="li"
              key={record.id ?? r.number}
              sx={{ border: 1, borderColor: "divider", borderRadius: 2, px: 1.5, py: 1, bgcolor: "background.default" }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1} flexWrap="wrap" useFlexGap>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                  {r.number}
                </Typography>
                <Typography variant="body2" fontWeight={600} sx={{ fontFamily: "monospace" }}>
                  {r.amount}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {[r.card, r.paidAt && `Paid ${r.paidAt}`].filter(Boolean).join(" · ")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {r.confirmedBy}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

Receipts.propTypes = { records: PropTypes.array.isRequired };

const ATTEMPTS_SHOWN = 3;

function Attempts({ attempts }) {
  const [all, setAll] = useState(false);
  const shown = all ? attempts : attempts.slice(0, ATTEMPTS_SHOWN);
  return (
    <Box component="section" aria-labelledby="case-attempts-title" mt={2}>
      <Divider sx={{ mb: 1.5 }} />
      <Typography id="case-attempts-title" variant="subtitle2" component="h3" mb={1}>
        Payment page opened
      </Typography>
      <Stack component="ul" spacing={1.25} sx={listSx} id="case-attempts-list">
        {shown.map((a) => {
          const said = outcome(a.outcome);
          return (
            <li key={a.id ?? a.created_at}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                <Box minWidth={0}>
                  <Typography variant="body2">{dateTime(a.created_at) || "Date not recorded"}</Typography>
                  <Typography variant="caption" color="text.secondary" component="p">
                    <Box component="span" sx={{ fontFamily: "monospace" }}>
                      {moneyIn(a.amount, a.currency)}
                    </Box>
                    {a.checked_at ? ` · last checked ${dateTime(a.checked_at)}` : ""}
                  </Typography>
                </Box>
                <Chip size="small" variant="outlined" color={said.tone} label={said.label} sx={{ flexShrink: 0 }} />
              </Stack>
            </li>
          );
        })}
      </Stack>
      {attempts.length > ATTEMPTS_SHOWN && (
        <ActionButton variant="text" sx={{ mt: 0.5, ml: -1.5 }} aria-expanded={all} aria-controls="case-attempts-list" onClick={() => setAll((v) => !v)}>
          {all ? "Show fewer" : `Show all ${attempts.length}`}
        </ActionButton>
      )}
    </Box>
  );
}

Attempts.propTypes = { attempts: PropTypes.array.isRequired };

Payment.propTypes = {
  caseData: PropTypes.object.isRequired,
  visas: PropTypes.object.isRequired,
  canEdit: PropTypes.bool,
  onChanged: PropTypes.func.isRequired,
};
